import { TextField as MuiTextField } from '@mui/material';
import { Amplify, Auth } from 'aws-amplify';
import { useController } from 'react-hook-form';
import type { UseControllerProps } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';
import type { TFunction } from 'i18next';
import { BondsmithUser } from '../../../features/auth/hooks/useAuth';
import { getFieldErrors } from '../../../form';

interface UploadTextFieldProps<T extends FieldValues> extends UseControllerProps<T> {
  readonly label: string;
  readonly type?: string;
  readonly disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly t: TFunction<any>;
}

export function FormUploadTextField<T extends FieldValues = FieldValues>(
  props: UploadTextFieldProps<T>
) {
  const { name, label, t, type, disabled } = props;
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    field: { ref, value, ...field },
    formState,
  } = useController(props);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0];

    if (!file) {
      return;
    }

    try {
      const userPoolId = process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || '';
      const bucketName = process.env.NEXT_PUBLIC_PUBLIC_ASSETS_BUCKET_NAME || '';
      const region = userPoolId.split('_')[0];

      const user: BondsmithUser = await Auth.currentAuthenticatedUser();
      const object = await Amplify.Storage.put(
        `${user.attributes.sub}/${file.name}`,
        file,
        file.type
      );
      field.onChange(
        encodeURI(
          `https://${bucketName}.${region}.amazonaws.com/portal/${object.key}`
        )
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error uploading file', error);
    }
  };

  const errors = getFieldErrors(name, formState, t);

  return (
    <MuiTextField
      InputLabelProps={{
        shrink: true,
      }}
      type={type}
      id={name}
      label={label}
      inputRef={ref}
      {...field}
      {...errors}
      onChange={handleChange}
      disabled={disabled || errors.disabled}
    />
  );
}
